import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {AccessibleGridCell, Container, Grid, GridType} from 'constants/SampleConstants';
import {getBlockingStatusWarningTooltip} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {SampleStatusActionType} from 'inventory/sample-status/SampleStatusWarningTracking';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {MeasurementType} from 'samples/MeasurementForm';
import * as StorableActions from 'samples/StorableActions';
import Tooltip from 'ui-library/bs/Tooltip';
import InlineField from 'ui-library/fields/InlineField';
import {Header1} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ConfirmableMeasurementField from '../ConfirmableMeasurementField';
import ContainerContentTables from '../ContainerContentTables';
import * as SampleUtil from '../SampleUtil';

function OpenContainerLink({container}: {container: Container}): JSX.Element {
  return (
    <Tooltip title="Open container">
      <a
        className="u-base-text u-tiny-spacing-left"
        onClick={() => {
          StorableActions.open(container);
        }}
      >
        <BenchlingIcon icon="upload" />
      </a>
    </Tooltip>
  );
}

async function checkSampleStatus({
  container,
  actionAttempted,
  eventGroupId,
}: {
  container: Container;
  actionAttempted: SampleStatusActionType;
  eventGroupId: string;
}): Promise<boolean> {
  const {shouldProceed} = await callSampleStatusWarningModal({
    sampleIds: container ? [container.id] : [],
    trackingParams: {actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI, actionAttempted, eventGroupId},
  });
  return shouldProceed;
}

export interface GridCellDetailsProps {
  gridType: GridType;
  grid: Grid;
  isWritable: boolean;
  cells: Array<AccessibleGridCell>;
}

export default function GridCellDetails({
  gridType,
  grid,
  isWritable,
  cells,
}: GridCellDetailsProps): JSX.Element {
  const gridWidth = grid.cells[0].length;
  return (
    <div className="u-padding-top">
      {cells.map((cell, i) => (
        <div className="u-spacing-top" key={i}>
          <Header1>
            {SampleUtil.formatGridPosition({
              gridType,
              rowIndex: cell.rowIndex,
              columnIndex: cell.columnIndex,
              width: gridWidth,
              includeNoun: true,
            })}
            <OpenContainerLink container={cell.container} />
          </Header1>
          <div className="u-flex-display">
            {!SampleUtil.isFixedPlate(grid) && (
              <InlineField className="u-flex-grow" label="Barcode">
                {cell.container.barcode || <span className="u-faded">No Barcode</span>}
              </InlineField>
            )}
            <InlineField className="u-flex-grow" label={getQuantityDisplayString()}>
              <ConfirmableMeasurementField
                isDisabled={!isWritable}
                measurement={cell.container.quantity}
                onComplete={(quantity) => {
                  StorableActions.updateContainerWithDataMods(cell.container.id, {quantity});
                }}
                measurementType={MeasurementType.QUANTITY}
                confirmBeforeStartEditing={() =>
                  checkSampleStatus({
                    container: cell.container,
                    actionAttempted: 'update_quantity',
                    eventGroupId: Analytics.makeAnalyticsEventId(),
                  })
                }
                disableEditsWithTooltip={getBlockingStatusWarningTooltip(cell.container)}
              />
            </InlineField>
          </div>
          {cell.container.contents.length > 0 ? (
            <ContainerContentTables
              containerId={cell.container.id}
              checkSampleStatus={({
                actionAttempted,
                eventGroupId,
              }: {
                actionAttempted: SampleStatusActionType;
                eventGroupId: string;
              }) => checkSampleStatus({container: cell.container, actionAttempted, eventGroupId})}
              disableEditsWithTooltip={getBlockingStatusWarningTooltip(cell.container)}
              isWritable={isWritable}
            />
          ) : (
            <div>
              No contents in{' '}
              {SampleUtil.formatGridPosition({
                gridType,
                rowIndex: cell.rowIndex,
                columnIndex: cell.columnIndex,
                width: gridWidth,
                includeNoun: true,
              })}
              .
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
